import { useLayoutEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Descendant } from 'slate';

import { Loader, Paper, Title } from '@mantine/core';

import { EmailTemplateType } from '@interfaces/templates.interface';

import {
  useGetEmailTemplateQuery,
  useUpsertEmailTemplateMutation,
} from '@api/templates.api';

import { LayoutContextType } from '@components/layout/Layout';
import WYSIWYG from '@components/WYSIWYG';

const defaultNewAccountContent: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: 'Gentile {{cliente}},' }],
  },
  {
    type: 'paragraph',
    children: [
      { text: 'grazie per esserti registrato nel sito prodottigianni.com' },
    ],
  },
  {
    type: 'paragraph',
    children: [
      {
        text: 'Per completare la registrazione, ti invitiamo a verificare il tuo indirizzo email, premendo nel seguente link:',
      },
    ],
  },
  {
    type: 'paragraph',
    children: [{ text: 'Verifica email', link: '{{confirmLink}}' }],
  },
];

const defaultAccountApprovedContent: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: 'Gentile {{cliente}},' }],
  },
  {
    type: 'paragraph',
    children: [{ text: 'la tua email è stata verificata con successo.' }],
  },
  {
    type: 'paragraph',
    children: [
      {
        text: "Il tuo account è in fase di approvazione. Riceverai un'email quando sarà approvato.",
      },
    ],
  },
];

export default function EmailSettings() {
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  const { data: newAccountTemplate, isLoading: isLoadingNewAccountTemplate } =
    useGetEmailTemplateQuery('newAccount');
  const {
    data: accountApprovedTemplate,
    isLoading: isLoadingAccountApprovedTemplate,
  } = useGetEmailTemplateQuery('accountApproved');

  const [upsertEmailTemplate, { isLoading: isSaving }] =
    useUpsertEmailTemplateMutation();

  const handleSaveTemplate = (
    type: EmailTemplateType,
    value: string,
    html: string,
  ) => {
    upsertEmailTemplate({
      type,
      body: { slateContent: value, html },
    });
  };

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Impostazioni email',
    });
  }, [setLayoutProps]);

  return (
    <>
      <Paper withBorder p="lg">
        <Title order={3} mb="xl">
          Testo richiesta verifica email nuovo utente
        </Title>
        {isLoadingNewAccountTemplate ? (
          <Loader />
        ) : (
          <WYSIWYG
            initialValue={
              newAccountTemplate
                ? JSON.parse(newAccountTemplate.slateContent)
                : defaultNewAccountContent
            }
            dynamicVariables={['cliente']}
            dynamicLinks={[
              { href: '{{confirmLink}}', label: 'Link verifica email' },
            ]}
            onSave={(value, html) => {
              handleSaveTemplate('newAccount', value, html);
            }}
            isLoading={isSaving}
          />
        )}
      </Paper>
      <Paper mt="xl" withBorder p="lg">
        <Title order={3} mb="xl">
          Testo conferma verifica email
        </Title>
        {isLoadingAccountApprovedTemplate ? (
          <Loader />
        ) : (
          <WYSIWYG
            initialValue={
              accountApprovedTemplate
                ? JSON.parse(accountApprovedTemplate.slateContent)
                : defaultAccountApprovedContent
            }
            dynamicVariables={['cliente']}
            onSave={(value, html) => {
              handleSaveTemplate('accountApproved', value, html);
            }}
            isLoading={isSaving}
          />
        )}
      </Paper>
    </>
  );
}
